'use client';

import { PageShell } from '@/components/layout/page-shell';
import { Badge } from '@/components/ui/badge';
import { UnlockCta } from '@/features/freemium/components/unlock-cta';
import { BUNNY_CDN } from '@/shared/constants/bunny';
import { getCountryName } from '@/shared/utils/country';
import { cn, formatDuration } from '@/shared/utils/formatting';
import { getChoosePlanHrefForCreator } from '@/shared/utils/routes';
import { Globe, Play } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { getVideoTitleDisplay } from '@/shared/utils/video-title';
import type { CreatorFeaturedVideoProps } from './types';

/** Extract "City, Country" from a full geocoded address string */
function shortenLocation(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length <= 2) return raw;
  const country = parts.at(-1) ?? '';
  // Walk backwards to find city — skip postal codes (digits + dashes)
  for (let i = parts.length - 2; i >= 0; i--) {
    const part = parts[i];
    if (!part) continue;
    const segment = part.replace(/\s*-\s*[A-Z]{2}$/, '').trim(); // strip " - PB" state codes
    if (!/^\d[\d\s-]*$/.test(segment) && segment.length > 1) {
      return `${segment}, ${country}`;
    }
  }
  return country || null;
}

// Locations are "City, Region, Country" strings — the last segment is the
// country name the globe's country resolver accepts (same contract as
// community-post's FRONT-250 filter half).
function getLocationCountry(location: string | null): string | undefined {
  const country = location?.split(',').at(-1)?.trim();
  return country || undefined;
}

export function CreatorFeaturedVideo({
  video,
  isFree,
  canWatch,
  handler,
}: CreatorFeaturedVideoProps) {
  const locationLabel = shortenLocation(getCountryName(video.location));
  // `focus` flies the globe to this video's pin and opens its card, but only
  // exists as a pin when the video carries coords; `countries` drives the
  // filter (lat/lng alone would move the camera and filter nothing).
  const globeCountry = getLocationCountry(video.location);
  const hasCoords = Boolean(video.latitude && video.longitude);
  const router = useRouter();
  const playable = canWatch || isFree;
  const destination = playable ? `/videos/${video.uuid}` : getChoosePlanHrefForCreator(handler);

  const [hovering, setHovering] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);

  const previewUrl = video.bunny_video_id
    ? `${BUNNY_CDN}/${video.bunny_video_id}/preview.webp`
    : null;
  const hasPreview = previewUrl && !previewFailed;
  const showPreview = hovering && previewLoaded;

  return (
    <section>
      <PageShell spacing="none">
        <div
          role="link"
          tabIndex={0}
          onClick={() => router.push(destination)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') router.push(destination);
          }}
          className={cn(
            'flex flex-col md:flex-row gap-0 md:gap-0 cursor-pointer',
            'rounded overflow-hidden bg-white/5',
            'transition-all duration-250',
            'hover:bg-white/8',
            !playable && 'shadow-[0_22px_54px_rgba(0,0,0,0.55)]'
          )}
          {...(!playable
            ? {
                style: {
                  background: 'linear-gradient(135deg, rgba(171,0,19,0.07), #0d0d0d 55%)',
                },
              }
            : {})}
        >
          {/* Thumbnail — 55% on desktop, full width on mobile */}
          <div
            className="group/thumb relative md:w-[55%] shrink-0 aspect-video md:aspect-auto md:min-h-[420px]"
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
          >
            <Image
              src={video.thumbnail || video.thumbnail_webp || ''}
              alt={getVideoTitleDisplay(video.title)}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 55vw"
              loading="eager"
              fetchPriority="high"
            />

            {/* Animated preview — fades in on hover */}
            {hasPreview && (
              <img
                src={previewUrl}
                alt=""
                loading="lazy"
                onLoad={() => setPreviewLoaded(true)}
                onError={() => setPreviewFailed(true)}
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-200 z-[1]"
                style={{ opacity: showPreview ? 1 : 0 }}
              />
            )}

            {/* Gradient overlay */}
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.4) 100%)',
              }}
            />

            {/* Play button — frosted glass */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div
                className={cn(
                  'rounded-full flex items-center justify-center',
                  'w-12 h-12 sm:w-[52px] sm:h-[52px]',
                  'bg-white/12 border border-white/22 backdrop-blur-sm',
                  'transition-all duration-150',
                  'group-hover/thumb:scale-[1.08] group-hover/thumb:bg-[rgba(171,0,19,0.85)]'
                )}
              >
                <Play className="w-[18px] h-[18px] text-white fill-white" />
              </div>
            </div>

            {/* "Latest" badge — top left */}
            <Badge variant="accent" size="sm" className="absolute top-3 left-3">
              Latest
            </Badge>

            {/* Location — bottom left */}
            {locationLabel && (
              <div className="absolute bottom-3 left-3 inline-flex items-center gap-1 px-2 py-1 bg-black/75 rounded text-xs text-white/80 uppercase tracking-wide max-w-[60%] line-clamp-1">
                <Globe className="w-3 h-3 shrink-0" /> {locationLabel}
              </div>
            )}

            {/* Duration — bottom right */}
            {video.duration && (
              <Badge variant="overlay" size="sm" className="absolute bottom-3 right-3">
                {formatDuration(video.duration)}
              </Badge>
            )}
          </div>

          {/* Info — 45% on desktop */}
          <div
            className="flex-1 p-5 sm:p-7 flex flex-col justify-center"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(13,13,13,1) 35%)',
            }}
          >
            {/* Subtitle */}
            <div className="text-[10px] font-bold tracking-widest uppercase text-red-primary/70 mb-2.5">
              Featured{isFree ? ' · Free' : ''}
            </div>

            <h3 className="text-lg sm:text-xl font-bold tracking-tight leading-tight text-[#f0ede8] mb-2.5 line-clamp-2">
              {getVideoTitleDisplay(video.title)}
            </h3>

            {video.description && (
              <p className="text-xs leading-relaxed text-white/38 mb-5 line-clamp-3 max-w-[340px]">
                {video.description}
              </p>
            )}

            {locationLabel && (
              <Link
                href={{
                  pathname: '/globe',
                  query: {
                    ...(hasCoords ? { focus: video.uuid } : {}),
                    ...(globeCountry ? { countries: globeCountry } : {}),
                  },
                }}
                onClick={(e) => e.stopPropagation()}
                className="relative z-10 inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors mb-4"
              >
                <Globe className="w-3 h-3" />
                <span>{locationLabel}</span>
              </Link>
            )}

            <div className="flex items-center gap-2.5">
              {playable ? (
                /* Frosted glass CTA */
                <div
                  role="button"
                  tabIndex={-1}
                  className={cn(
                    'group/btn inline-flex items-center gap-2.5 rounded',
                    'px-4 py-2 text-[13px] font-semibold tracking-wide',
                    'bg-white/7 border border-white/18 backdrop-blur-md',
                    'text-white/75 cursor-pointer',
                    'transition-all duration-150',
                    'hover:bg-[rgba(204,34,34,0.13)] hover:border-[rgba(204,34,34,0.55)]',
                    'hover:text-[#f0ede8] hover:shadow-[0_0_0_1px_rgba(204,34,34,0.18),inset_0_0_16px_rgba(204,34,34,0.06)]'
                  )}
                >
                  <span
                    className={cn(
                      'w-[22px] h-[22px] rounded flex items-center justify-center shrink-0',
                      'bg-white/12 border border-white/15',
                      'transition-all duration-150',
                      'group-hover/btn:bg-[rgba(204,34,34,0.85)] group-hover/btn:border-transparent'
                    )}
                  >
                    <Play className="w-[9px] h-[9px] fill-white text-white" />
                  </span>
                  Watch now
                </div>
              ) : (
                <span onClick={(e) => e.stopPropagation()} className="relative z-10">
                  <UnlockCta
                    label="Unlock to watch"
                    source="featured_video"
                    handler={handler}
                    href={destination}
                  />
                </span>
              )}

              {video.published_at && (
                <span className="text-[11px] text-white/40">
                  {new Date(video.published_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              )}
            </div>
          </div>
        </div>
      </PageShell>
    </section>
  );
}
